import { collection, getDocs, getCountFromServer, collectionGroup, query, where, documentId } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Cruise } from '../context/CruiseContext';

export interface TopUser {
  userId: string;
  displayName: string;
  email: string;
  cruiseCount: number;
  pastCruises: number;
  futureCruises: number;
}

export interface SystemAnalytics {
  totalUsers: number;
  totalFriendProfiles: number;
  totalFriendships: number;
  totalCruises: number;
  usersWithCruises: number;
  pastCruises: number;
  futureCruises: number;
  topCruiseLines: { name: string; count: number }[];
  topShips: { name: string; count: number }[];
  topUsers: TopUser[];
}

export const getSystemAnalytics = async (): Promise<SystemAnalytics> => {
  const testUserIds = new Set<string>();

  try {
    // Fetch all accounts flagged as test accounts
    const testUsersQuery = query(collection(db, 'users'), where('isTestAccount', '==', true));
    const testUsersSnap = await getDocs(testUsersQuery);
    testUsersSnap.docs.forEach(doc => {
      testUserIds.add(doc.id);
    });
  } catch (error) {
    console.error('Error fetching test users:', error);
  }

  // 1. Server Aggregations (Fast & Cheap)
  let totalUsers = 0;
  let totalFriendProfiles = 0;
  let totalFriendships = 0;

  try {
    const usersCountSnap = await getCountFromServer(collection(db, 'users'));
    totalUsers = usersCountSnap.data().count - testUserIds.size;
  } catch (error) {
    console.error('Error fetching users count:', error);
  }

  try {
    const friendProfilesSnap = await getCountFromServer(collection(db, 'friendLookup'));
    totalFriendProfiles = friendProfilesSnap.data().count / 2;
  } catch (error) {
    console.error('Error fetching friend profiles count:', error);
  }

  try {
    const friendshipsSnap = await getCountFromServer(collectionGroup(db, 'friends'));
    totalFriendships = friendshipsSnap.data().count / 2;
  } catch (error) {
    console.error('Error fetching friendships count (collectionGroup):', error);
  }

  // 2. Memory Aggregations for Cruises
  let totalCruises = 0;
  const uniqueUsersWithCruises = new Set<string>();
  let pastCruises = 0;
  let futureCruises = 0;
  const cruiseLineCounts: Record<string, number> = {};
  const shipCounts: Record<string, number> = {};
  const userCruiseStats: Record<string, { total: number; past: number; future: number }> = {};
  const topCruiseLines: { name: string; count: number }[] = [];
  const topShips: { name: string; count: number }[] = [];
  const topUsers: TopUser[] = [];

  try {
    const cruisesSnap = await getDocs(collection(db, 'cruises'));
    const allCruises = cruisesSnap.docs.map(d => d.data() as Cruise);

    totalCruises = allCruises.length;
    const todayStr = new Date().toISOString().split('T')[0];

    allCruises.forEach(cruise => {
      // Exclude cruises belonging to test accounts
      if (testUserIds.has(cruise.userId)) return;

      uniqueUsersWithCruises.add(cruise.userId);

      if (cruise.departureDate < todayStr) {
        pastCruises++;
      } else {
        futureCruises++;
      }

      if (cruise.cruiseLine) {
        cruiseLineCounts[cruise.cruiseLine] = (cruiseLineCounts[cruise.cruiseLine] || 0) + 1;
      }

      if (cruise.shipName) {
        shipCounts[cruise.shipName] = (shipCounts[cruise.shipName] || 0) + 1;
      }

      if (!userCruiseStats[cruise.userId]) {
        userCruiseStats[cruise.userId] = { total: 0, past: 0, future: 0 };
      }
      userCruiseStats[cruise.userId].total += 1;
      if (cruise.departureDate < todayStr) {
        userCruiseStats[cruise.userId].past += 1;
      } else {
        userCruiseStats[cruise.userId].future += 1;
      }
    });

    const sortedCruiseLines = Object.entries(cruiseLineCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    topCruiseLines.push(...sortedCruiseLines);

    const sortedShips = Object.entries(shipCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    topShips.push(...sortedShips);

    const topUserIds = Object.entries(userCruiseStats)
      .map(([userId, stats]) => ({ userId, ...stats }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 100); // Fetch up to 100 users for pagination

    if (topUserIds.length > 0) {
      try {
        const userIdsToFetch = topUserIds.map(u => u.userId);
        
        // Chunk user IDs into groups of 10 for Firestore 'in' queries
        const chunks: string[][] = [];
        for (let i = 0; i < userIdsToFetch.length; i += 10) {
          chunks.push(userIdsToFetch.slice(i, i + 10));
        }

        const userMap: Record<string, any> = {};
        await Promise.all(chunks.map(async (chunk) => {
          const usersQuery = query(collection(db, 'users'), where(documentId(), 'in', chunk));
          const usersSnap = await getDocs(usersQuery);
          usersSnap.docs.forEach(doc => {
            userMap[doc.id] = doc.data();
          });
        }));

        const friendLookupMap: Record<string, any> = {};
        try {
          await Promise.all(chunks.map(async (chunk) => {
            const lookupQuery = query(collection(db, 'friendLookup'), where('uid', 'in', chunk));
            const lookupSnap = await getDocs(lookupQuery);
            lookupSnap.docs.forEach(doc => {
              const data = doc.data();
              friendLookupMap[data.uid] = data;
            });
          }));
        } catch (err) {
          console.log('[AdminAnalytics] Could not fetch friend lookups for top users fallback', err);
        }

        topUserIds.forEach(({ userId, total, past, future }) => {
          const userData = userMap[userId];
          const lookupData = friendLookupMap[userId];
          
          const finalEmail = userData?.email || userData?.consentData?.email || lookupData?.email || `UID: ${userId.substring(0, 8)}...`;
          
          topUsers.push({
            userId,
            cruiseCount: total,
            pastCruises: past,
            futureCruises: future,
            displayName: userData?.preferredDisplayName || userData?.displayName || lookupData?.username || 'Unknown User',
            email: finalEmail
          });
        });
      } catch (err) {
        console.error('Error fetching top users details:', err);
      }
    }
  } catch (error) {
    console.error('Error fetching cruises:', error);
  }

  return {
    totalUsers,
    totalFriendProfiles,
    totalFriendships,
    totalCruises,
    usersWithCruises: uniqueUsersWithCruises.size,
    pastCruises,
    futureCruises,
    topCruiseLines,
    topShips,
    topUsers
  };
};
